// Centralized brand-logo registry.
//
// Backed by `react-icons` — combines Font Awesome 6 (Fa*), Simple Icons
// (Si*), and Tabler Icons (Tb*) so every brand uses one consistent
// monochrome SVG renderer (`currentColor`), sized by `className`.
//
// Mirrors the integration surface in /Poly/.env so adding a new
// provider in the platform = one entry here in the website.
//
// Brands without a real icon in any pack fall back to the generic
// Lucide `Plug` symbol, keeping the visual rhythm consistent.
import {
    FaInstagram,
    FaSquareFacebook,
    FaLinkedin,
    FaTiktok,
    FaYoutube,
    FaXTwitter,
    FaWhatsapp,
    FaThreads,
    FaTelegram,
    FaDiscord,
    FaGithub,
    FaMeta,
    FaGoogle,
    FaMicrosoft,
    FaSlack,
    FaShopify,
    FaStripe,
    FaPaypal,
    FaSquare,
    FaAmazon,
    FaEtsy,
    FaGoogleDrive,
    FaTrello,
    FaJira,
    FaDropbox,
    FaAws,
    FaDocker,
    FaSalesforce,
    FaHubspot,
} from "react-icons/fa6";
import {
    SiAsana,
    SiNextdotjs,
    SiThreedotjs,
    SiOpencv,
    SiPython,
    SiTypescript,
    SiReact,
    SiFastapi,
    SiVite,
    SiLangchain,
    SiPrisma,
    SiVercel,
    SiClerk,
    SiNotion,
    SiCalendly,
    SiCaldotcom,
    SiGooglesheets,
    SiOpenai,
    SiAnthropic,
    SiClaude,
    SiGooglegemini,
    SiMistralai,
    SiElevenlabs,
    SiReplicate,
    SiHuggingface,
    SiKubernetes,
    SiMongodb,
    SiPostgresql,
    SiRedis,
    SiZoom,
    SiTwilio,
    SiGooglemeet,
    SiZendesk,
    SiIntercom,
    SiQuickbooks,
    SiXero,
    SiWoocommerce,
    SiAirtable,
    SiMailchimp,
    SiGoogleanalytics,
    SiTypeform,
    SiTwenty,
    SiCrunchbase,
    SiPexels,
    SiLivekit,
    SiPosthog,
    SiSentry,
    SiNgrok,
    SiApollographql,
} from "react-icons/si";
import { TbBrandAzure } from "react-icons/tb";
import { Plug, BrainCircuit } from "lucide-react";
import type { ComponentType, SVGProps } from "react";

type IconCmp = ComponentType<SVGProps<SVGSVGElement>>;

// Custom SVG icons for brands not available in react-icons/si.
// Paths sourced from simple-icons@16 (npm) — official brand marks, monochrome.
// xAI/Grok has no entry in simple-icons or any major icon library as of v16.20.0;
// it renders as a text-based "xAI" badge until an official icon is published.

/** DeepSeek — official path from simple-icons@16 */
function DeepSeekIcon(props: SVGProps<SVGSVGElement>) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path d="M23.748 4.651c-.254-.124-.364.113-.512.233-.051.04-.094.09-.137.137-.372.397-.806.657-1.373.626-.829-.046-1.537.214-2.163.848-.133-.782-.575-1.248-1.247-1.548-.352-.155-.708-.311-.955-.65-.172-.24-.219-.509-.305-.774-.055-.16-.11-.323-.293-.35-.2-.031-.278.136-.356.276-.313.572-.434 1.202-.422 1.84.027 1.436.633 2.58 1.838 3.393.137.094.172.187.129.323-.082.28-.18.553-.266.833-.055.179-.137.218-.328.14a5.5 5.5 0 0 1-1.737-1.179c-.857-.828-1.631-1.743-2.597-2.46a12 12 0 0 0-.689-.47c-.985-.957.13-1.743.387-1.836.27-.098.094-.433-.778-.428-.872.003-1.67.295-2.687.685a3 3 0 0 1-.465.136 9.6 9.6 0 0 0-2.883-.101c-1.885.21-3.39 1.1-4.497 2.622C.082 8.776-.231 10.854.152 13.02c.403 2.284 1.568 4.175 3.36 5.653 1.857 1.533 3.997 2.284 6.438 2.14 1.482-.085 3.132-.284 4.994-1.86.47.234.962.328 1.78.398.629.058 1.235-.031 1.705-.129.735-.155.684-.836.418-.961-2.155-1.004-1.682-.595-2.112-.926 1.095-1.295 2.768-3.598 3.284-6.733.05-.346.115-.834.108-1.114-.004-.171.035-.238.23-.257a4.2 4.2 0 0 0 1.545-.475c1.397-.763 1.96-2.016 2.093-3.517.02-.23-.004-.467-.247-.588M11.58 18.168c-2.088-1.642-3.101-2.183-3.52-2.16-.39.024-.32.472-.234.763.09.288.207.487.371.74.114.167.192.416-.113.603-.673.416-1.842-.14-1.897-.168-1.361-.801-2.5-1.86-3.301-3.306-.775-1.393-1.225-2.888-1.299-4.482-.02-.385.094-.522.477-.592a4.7 4.7 0 0 1 1.53-.038c2.131.311 3.946 1.264 5.467 2.774.868.86 1.525 1.887 2.202 2.89.72 1.066 1.494 2.082 2.48 2.915.348.291.626.513.892.677-.802.09-2.14.109-3.055-.615zm1.001-6.44a.306.306 0 0 1 .415-.287.3.3 0 0 1 .113.074.3.3 0 0 1 .086.214c0 .17-.136.307-.308.307a.303.303 0 0 1-.306-.307m3.11 1.596c-.2.081-.4.151-.591.16a1.25 1.25 0 0 1-.798-.254c-.274-.23-.47-.358-.551-.758a1.7 1.7 0 0 1 .015-.588c.07-.327-.007-.537-.238-.727-.188-.156-.426-.199-.689-.199a.6.6 0 0 1-.254-.078.253.253 0 0 1-.114-.358 1 1 0 0 1 .192-.21c.356-.202.767-.136 1.146.016.352.144.618.408 1.001.782.392.451.462.576.685.915.176.264.336.536.446.848.066.194-.02.353-.25.45" />
        </svg>
    );
}

/** Moonshot AI — official path from simple-icons@16 */
function MoonshotIcon(props: SVGProps<SVGSVGElement>) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path d="m1.053 16.91 9.538 2.55a21 20.981 0 0 0 .06 2.031l5.956 1.592a12 11.99 0 0 1-15.554-6.172m-1.02-5.79 11.352 3.035a21 20.981 0 0 0-.469 2.01l10.817 2.89a12 11.99 0 0 1-1.845 2.004L.658 15.918a12 11.99 0 0 1-.625-4.796m1.593-5.146L13.573 9.17a21 20.981 0 0 0-1.01 1.874l11.297 3.02a21 20.981 0 0 1-.67 2.362l-11.55-3.087L.125 10.26a12 11.99 0 0 1 1.499-4.285ZM6.067 1.58l11.285 3.016a21 20.981 0 0 0-1.688 1.719l7.824 2.091a21 20.981 0 0 1 .513 2.664L2.107 5.218a12 11.99 0 0 1 3.96-3.638M21.68 4.866 7.222 1.003A12 11.99 0 0 1 21.68 4.866" />
        </svg>
    );
}

/** xAI — no official icon exists in simple-icons v16 or any major icon library.
 *  Renders a minimal "xAI" text badge until an official SVG mark is published. */
function XaiIcon(props: SVGProps<SVGSVGElement>) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
            <text x="2" y="17" fontSize="9" fontWeight="700" fontFamily="system-ui,sans-serif" fill="currentColor">xAI</text>
        </svg>
    );
}

/** Qdrant — official path from simple-icons@16 */
function QdrantIcon(props: SVGProps<SVGSVGElement>) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path d="m12 16.5 3.897-2.25v-4.5L12 7.5 8.103 9.75v4.5zM1.607 18 12 24l3.897-2.25v-4.5L12 19.5l-6.495-3.75v-7.5L12 4.5l6.495 3.75v15L22.393 21V6L12 0 1.607 6Z" />
        </svg>
    );
}

// Single source of truth — keys are lowercase, hyphen-free brand slugs.
export const BRAND_REGISTRY = {
    // ─── Social / messaging ──────────────────────────────────────
    instagram: FaInstagram,
    facebook: FaSquareFacebook,
    linkedin: FaLinkedin,
    tiktok: FaTiktok,
    youtube: FaYoutube,
    twitter: FaXTwitter,
    x: FaXTwitter,
    whatsapp: FaWhatsapp,
    threads: FaThreads,
    telegram: FaTelegram,
    discord: FaDiscord,
    messenger: FaSquareFacebook,
    meta: FaMeta,
    github: FaGithub,

    // ─── Email / outreach ────────────────────────────────────────
    gmail: FaGoogle,
    outlook: FaMicrosoft,
    mailchimp: SiMailchimp,
    autosend: Plug,
    apollo: SiApollographql,

    // ─── Comms / collaboration ───────────────────────────────────
    slack: FaSlack,
    zoom: SiZoom,
    teams: Plug,
    twilio: SiTwilio,
    googlemeet: SiGooglemeet,
    livekit: SiLivekit,

    // ─── CRM / support ───────────────────────────────────────────
    hubspot: FaHubspot,
    salesforce: FaSalesforce,
    twenty: SiTwenty,
    pipedrive: Plug,
    zendesk: SiZendesk,
    intercom: SiIntercom,
    freshdesk: Plug,
    crunchbase: SiCrunchbase,

    // ─── Finance / payments ──────────────────────────────────────
    stripe: FaStripe,
    quickbooks: SiQuickbooks,
    xero: SiXero,
    paypal: FaPaypal,
    square: FaSquare,

    // ─── E-commerce ──────────────────────────────────────────────
    shopify: FaShopify,
    woocommerce: SiWoocommerce,
    amazon: FaAmazon,
    etsy: FaEtsy,

    // ─── Productivity / data ─────────────────────────────────────
    notion: SiNotion,
    googledrive: FaGoogleDrive,
    googlesheets: SiGooglesheets,
    airtable: SiAirtable,
    asana: SiAsana,
    trello: FaTrello,
    monday: Plug,
    jira: FaJira,
    dropbox: FaDropbox,

    // ─── Scheduling / forms ──────────────────────────────────────
    calendly: SiCalendly,
    cal: SiCaldotcom,
    typeform: SiTypeform,

    // ─── AI models / studios ─────────────────────────────────────
    openai: SiOpenai,
    anthropic: SiAnthropic,
    claude: SiClaude,
    gemini: SiGooglegemini,
    mistral: SiMistralai,
    deepseek: DeepSeekIcon,
    elevenlabs: SiElevenlabs,
    heygen: Plug,
    kling: Plug,
    stablediffusion: Plug,
    replicate: SiReplicate,
    huggingface: SiHuggingface,
    litellm: Plug,
    sora: Plug,
    xai: XaiIcon,
    moonshot: MoonshotIcon,
    llms: BrainCircuit,

    // ─── Cloud / infra ───────────────────────────────────────────
    azure: TbBrandAzure,
    aws: FaAws,
    microsoft: FaMicrosoft,
    google: FaGoogle,
    docker: FaDocker,
    kubernetes: SiKubernetes,

    // ─── Databases ───────────────────────────────────────────────
    mongodb: SiMongodb,
    postgresql: SiPostgresql,
    redis: SiRedis,
    pinecone: Plug,
    qdrant: QdrantIcon,

    // ─── Browser / video / dev infra ─────────────────────────────
    steel: Plug,
    remotion: Plug,
    pexels: SiPexels,
    ngrok: SiNgrok,

    // ─── Analytics ───────────────────────────────────────────────
    googleanalytics: SiGoogleanalytics,
    posthog: SiPosthog,
    sentry: SiSentry,

    // ─── Search ──────────────────────────────────────────────────
    serper: Plug,

    // ─── Tech stack (case studies / portfolio context only) ──────
    // These appear in historical case studies, NOT as live Poly infra.
    nextjs: SiNextdotjs,
    react: SiReact,
    threejs: SiThreedotjs,
    opencv: SiOpencv,
    python: SiPython,
    typescript: SiTypescript,
    fastapi: SiFastapi,
    vite: SiVite,
    langchain: SiLangchain,
    prisma: SiPrisma,
    vercel: SiVercel,
    clerk: SiClerk,
} satisfies Record<string, IconCmp>;

export type BrandName = keyof typeof BRAND_REGISTRY;

type Props = {
    name: BrandName;
    className?: string;
};

export function BrandLogo({ name, className }: Props) {
    const Icon = BRAND_REGISTRY[name];
    return <Icon className={className} aria-hidden="true" />;
}
